import { Textarea } from "../componentes/Textarea"
import { Input } from "../componentes/Input"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  ErroDaApi,
  ROTULO_DA_FONTE,
  abrirLaudo,
  abrirPeca,
  api,
  baixarLaudo,
  baixarPeca,
  foiAbortado,
  type AvaliacaoNaTela,
  type EntradaDoProcesso,
  type FonteDoArquivo,
  type ProcessoDoContrato,
} from "../api"
import { dataHora, dia, hora, inteiro, nivelDeAssinatura, numeroComAno, tamanho } from "../formato"
import { Abas, PainelDaAba, type DescricaoDeAba } from "../componentes/Abas"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../componentes/Card"
import { ConfirmarComSenha, ResumoDoAto } from "../componentes/ConfirmarComSenha"
import { Cronologia, ItemDaCronologia } from "../componentes/Cronologia"
import { FiltroComBusca } from "../componentes/FiltroComBusca"
import { GrupoDaLinhaDoTempo, ItemDaLinhaDoTempo, LinhaDoTempo } from "../componentes/LinhaDoTempo"
import { ToggleGroup, ToggleGroupItem } from "../componentes/ToggleGroup"
import * as Icones from "../componentes/Icones"
import { Selo, chaveConhecida } from "../componentes/Selo"
import { MarcaDeObrigatorio } from "../componentes/RotuloDoCampo"
import { BOTAO_BRANCO, MAIUSCULA, ROTULO_DE_CAMPO, TEXTO_DO_CADASTRO, VAZIO } from "../lib/classes"
import { cn } from "../lib/cn"

const RECORTE_TUDO = "tudo"
const RECORTE_FISCALIZACAO = "fiscalizacao"
const TIPO_LAUDO = "laudo"

const DECISOES = [
  {
    valor: "validada",
    rotulo: "Validar",
    Icone: Icones.Validada,
    titulo: "A peça está correta e serve à fiscalização.",
  },
  {
    valor: "recusada",
    rotulo: "Recusar",
    Icone: Icones.Recusada,
    titulo: "A peça não serve. Quem responde pelo contrato é avisado.",
  },
  {
    valor: "com_ressalva",
    rotulo: "Com ressalva",
    Icone: Icones.Ressalva,
    titulo: "A peça serve, com o que ficar escrito no laudo.",
  },
  {
    valor: "anotacao",
    rotulo: "Anotação",
    Icone: Icones.Anotacao,
    titulo: "Registra o que o fiscal viu, sem aprovar nem reprovar.",
  },
] as const

function chaveDaEntrada(entrada: EntradaDoProcesso): string {
  return `${entrada.tipo_entrada}-${entrada.id}`
}

const APOIO = "mt-1 text-(length:--texto-xs) text-(--tinta-3)"
const FAIXA_CRITICA = "rounded-(--raio-controle) border border-(--vermelho) bg-(--vermelho-fundo) px-[15px] py-[13px] text-[0.8125rem] [&_p]:mt-1.5"
const NO_MIOLO = "mx-auto my-6 h-fit max-w-[44rem]"
const MIOLO = "flex min-w-0 flex-auto flex-col"
const CANCELAR = "border-destructive text-destructive hover:text-destructive"
const SUCESSO = "bg-status-sucesso text-sobre-status-sucesso hover:bg-status-sucesso-pressionado"

const COR_DA_DECISAO: Record<(typeof DECISOES)[number]["valor"], { variant: "default" | "outline"; className: string }> = {
  validada: { variant: "default", className: SUCESSO },
  recusada: { variant: "outline", className: CANCELAR },
  com_ressalva: { variant: "default", className: "bg-status-atencao text-sobre-status-atencao hover:bg-status-atencao" }, // anti-ia-ok: token de cor de status, não texto de tela
  anotacao: { variant: "default", className: "" },
}

const COR_DO_ICONE: Record<(typeof DECISOES)[number]["valor"], string> = {
  validada: "text-status-sucesso",
  recusada: "text-destructive",
  com_ressalva: "text-status-atencao",
  anotacao: "text-(--acao)",
}

const TETO_DA_LEMBRANCA = 8

type NaLembranca = { arquivo: Blob; fonte: FonteDoArquivo | null }
type Lembranca = React.RefObject<Map<string, NaLembranca>>

function guardarNaLembranca(
  lembranca: Lembranca,
  chave: string,
  item: NaLembranca,
): void {
  const mapa = lembranca.current
  if (mapa === null) return
  mapa.delete(chave)
  mapa.set(chave, item)
  while (mapa.size > TETO_DA_LEMBRANCA) {
    const maisAntiga = mapa.keys().next()
    if (maisAntiga.done === true) break
    mapa.delete(maisAntiga.value)
  }
}

export function Esteira({
  contratoId,
  cpfNaSessao,
  pecaId,
  aoSaberDoProcesso,
}: {
  contratoId: number
  cpfNaSessao: string
  pecaId?: number
  aoSaberDoProcesso?: (processo: string | null) => void
}) {
  const [processo, definirProcesso] = useState<ProcessoDoContrato | null>(null)
  const [erro, definirErro] = useState<string | null>(null)
  const [grupo, definirGrupo] = useState(RECORTE_TUDO)
  const [papel, definirPapel] = useState<string | null>(null)
  const [papeisDoProcesso, definirPapeisDoProcesso] = useState<{ papel: string; rotulo: string }[]>([])
  const [busca, definirBusca] = useState("")
  const [aberta, definirAberta] = useState<string | null>(
    pecaId === undefined ? null : `peca-${pecaId}`,
  )
  const [versao, definirVersao] = useState(0)

  const lembranca = useRef<Map<string, NaLembranca>>(new Map())

  useEffect(() => {
    let vivo = true
    const relogio = window.setTimeout(() => {
      api
        .processo(contratoId, grupo, busca)
        .then((corpo) => {
          if (!vivo) return
          definirProcesso(corpo)
          definirErro(null)
        })
        .catch((falha: unknown) => {
          if (vivo) definirErro(falha instanceof Error ? falha.message : "falha")
        })
    }, busca === "" ? 0 : 250)
    return () => {
      vivo = false
      window.clearTimeout(relogio)
    }
  }, [contratoId, grupo, busca, versao])

  useEffect(() => {
    let vivo = true
    api
      .processo(contratoId, RECORTE_TUDO, "")
      .then((corpo) => {
        if (vivo) definirPapeisDoProcesso(papeisNaOrdem(corpo.entradas))
      })
      .catch(() => undefined)
    return () => {
      vivo = false
    }
  }, [contratoId, versao])

  const [conferidasAgora, definirConferidasAgora] = useState<Record<string, string>>({})
  const doGrupo = useMemo(
    () =>
      emOrdemDecrescente(processo?.entradas ?? []).map((entrada) => {
        const conferidaEm = conferidasAgora[chaveDaEntrada(entrada)]
        return conferidaEm === undefined ? entrada : { ...entrada, pdf_conferido_em: conferidaEm }
      }),
    [processo, conferidasAgora],
  )
  const marcarConferida = useCallback((entrada: EntradaDoProcesso) => {
    const chave = chaveDaEntrada(entrada)
    definirConferidasAgora((antes) => ({ ...antes, [chave]: new Date().toISOString() }))
  }, [])
  const entradas = useMemo(
    () => (papel === null ? doGrupo : doGrupo.filter((entrada) => entrada.papel === papel)),
    [doGrupo, papel],
  )

  useEffect(() => {
    const primeira = entradas[0]
    if (primeira === undefined) return
    if (aberta !== null && entradas.some((e) => chaveDaEntrada(e) === aberta)) return
    definirAberta(chaveDaEntrada(primeira))
  }, [entradas, aberta])

  const posicao = entradas.findIndex((e) => chaveDaEntrada(e) === aberta)
  const atual = entradas[posicao] ?? null

  const andar = useCallback(
    (direcao: -1 | 1) => {
      if (entradas.length === 0) return
      const alvo = Math.min(Math.max(posicao + direcao, 0), entradas.length - 1)
      const proxima = entradas[alvo]
      if (proxima !== undefined) definirAberta(chaveDaEntrada(proxima))
    },
    [entradas, posicao],
  )

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      const alvo = evento.target as HTMLElement | null
      if (alvo && ["INPUT", "TEXTAREA", "SELECT"].includes(alvo.tagName)) return
      if (alvo?.closest('[role="dialog"]')) return
      if (evento.key === "ArrowDown") {
        evento.preventDefault()
        andar(1)
      }
      if (evento.key === "ArrowUp") {
        evento.preventDefault()
        andar(-1)
      }
    }
    window.addEventListener("keydown", aoTeclar)
    return () => window.removeEventListener("keydown", aoTeclar)
  }, [andar])

  const noProcesso =
    processo === null ? 0 : processo.no_catalogo + processo.fora_do_catalogo

  const processoDoContrato = processo?.processo_administrativo ?? null
  useEffect(() => {
    aoSaberDoProcesso?.(processoDoContrato)
  }, [aoSaberDoProcesso, processoDoContrato])

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      {erro !== null ? (
        <p className={cn(VAZIO, MAIUSCULA)}>Não foi possível abrir o processo: {erro}</p>
      ) : processo === null ? (
        <p className={VAZIO}>Carregando o processo...</p>
      ) : noProcesso === 0 ? (
        <section
          data-slot="esteira-vazia"
          className="flex min-h-[40vh] flex-col items-center justify-center gap-3 rounded-(--raio-cartao) border border-(--borda-forte) bg-(--superficie) px-6 py-10 text-center"
        >
          <span className="text-(--tinta-3)" aria-hidden="true">
            <Icones.TipoDePeca tamanho={36} />
          </span>
          <h2 className="text-(length:--texto-lg)">Nenhuma peça lida do ATHUS ainda</h2>
          <p className="max-w-[52ch] text-(length:--texto-sm) leading-(--linha-densa) text-(--tinta-2)">
            As peças deste processo ainda não foram trazidas do ATHUS.
          </p>
        </section>
      ) : (
        <div className="grid min-h-[24rem] min-w-0 grid-cols-1 items-start overflow-clip rounded-(--raio-cartao) border border-(--borda-forte) bg-(--superficie-2) min-[1101px]:flex-1 min-[1101px]:grid-cols-[380px_1fr] min-[1101px]:items-stretch">
          <aside
            className="relative flex max-h-[60vh] min-w-0 flex-col border-b border-(--borda-forte) bg-(--branco) min-[1101px]:max-h-none min-[1101px]:border-b-0"
            aria-label="Peças do processo"
          >
            <div className="flex min-h-0 flex-1 flex-col min-[1101px]:absolute min-[1101px]:inset-0">
            <div className="flex-none border-b border-(--borda) p-3">
              <div className="relative flex min-w-0">
                <span data-slot="lupa-da-busca" className="pointer-events-none absolute top-1/2 left-3 flex -translate-y-1/2 text-(--tinta-3)" aria-hidden="true">
                  <Icones.Buscar tamanho={16} />
                </span>
                <Input className={cn("pl-9 [&::-webkit-search-cancel-button]:hidden", busca !== "" && "pr-[88px]")}
                  type="search"
                  name="busca-no-processo"
                  autoComplete="off"
                  data-form-type="other"
                  spellCheck={false}
                  value={busca}
                  onChange={(evento) => definirBusca(evento.target.value)}
                  placeholder="Buscar por título, número, tipo ou autor"
                  aria-label="Buscar no processo"
                />
                {busca !== "" && (
                  <button
                    type="button"
                    className="absolute top-1/2 right-[5px] inline-flex -translate-y-1/2 cursor-pointer items-center gap-1 rounded-(--raio-controle) border border-(--borda) bg-(--branco) px-[7px] py-[3px] text-(length:--texto-xs) text-(--tinta-2) hover:border-(--borda-forte) hover:text-(--tinta) focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--foco)"
                    onClick={() => definirBusca("")}
                  >
                    <Icones.Fechar />
                    Limpar
                  </button>
                )}
              </div>
              {processo.sincronizado_em !== null && (
                <p className="mt-2 text-center text-(length:--texto-xs) text-(--tinta-3) tabular-nums">
                  Lido do ATHUS em {dataHora(processo.sincronizado_em)}
                </p>
              )}
              {busca !== "" && (
                <p className="mt-2 text-(length:--texto-xs) text-(--tinta-3) tabular-nums" role="status">
                  {inteiro(noProcesso)} peça(s) no processo ·{" "}
                  {inteiro(entradas.length)} nesta busca
                </p>
              )}
            </div>

            <div className="flex flex-none flex-col gap-2 border-b border-(--borda) px-3 py-2">
              <ToggleGroup
                type="single"
                aria-label="Recorte do processo"
                value={grupo}
                onValueChange={(valor) => {
                  if (valor !== "") definirGrupo(valor)
                }}
                className="grid w-full grid-cols-3"
              >
                {recortesDoProcesso(processo, noProcesso).map((item) => (
                  <ToggleGroupItem
                    key={item.chave}
                    value={item.chave}
                    disabled={item.falta !== undefined}
                    title={item.falta}
                    className="gap-1.5 px-2 text-(length:--texto-sm) disabled:pointer-events-auto disabled:cursor-not-allowed"
                  >
                    {item.rotulo}
                    <span className="rounded-(--raio-pilula) bg-(--painel) px-1.5 text-(length:--texto-xs) text-(--tinta-2) tabular-nums group-data-[state=on]/opcao:bg-primary-foreground group-data-[state=on]/opcao:text-primary">
                      {inteiro(item.contagem)}
                    </span>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              {papeisDoProcesso.length > 0 && (
                <FiltroComBusca
                  rotulo="Papel"
                  placeholder="Buscar papel"
                  valor={papel}
                  aoEscolher={definirPapel}
                  opcoes={papeisDoProcesso.map((item) => ({
                    valor: item.papel,
                    rotulo: item.rotulo,
                    contagem: doGrupo.filter((entrada) => entrada.papel === item.papel).length,
                  }))}
                />
              )}
            </div>

            <ListaDeEntradas
              entradas={entradas}
              abertaId={aberta}
              busca={busca}
              aoAbrir={(entrada) => definirAberta(chaveDaEntrada(entrada))}
            />
            </div>
          </aside>

          <div className="flex min-w-0 flex-col border-l border-(--borda-forte) bg-(--superficie)">
            {atual === null ? (
              <div className="flex flex-col items-center gap-2 p-6 text-center">
                <p className={cn(VAZIO, "text-center")}>
                  {busca === ""
                    ? "Nenhuma peça neste recorte."
                    : `Nenhum dado encontrado com a pesquisa "${busca}". Limpe a busca para ver todas as peças do processo.`}
                </p>
                {pareceEmail(busca) && <ORastroDoGerenciador />}
              </div>
            ) : (
              <PainelDaPeca
                contratoId={contratoId}
                cpfNaSessao={cpfNaSessao}
                peca={atual}
                posicao={posicao}
                total={entradas.length}
                lembranca={lembranca}
                aoAndar={andar}
                aoAvaliar={() => definirVersao((n) => n + 1)}
                aoConferir={marcarConferida}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function pareceEmail(texto: string): boolean {
  return /^\s*[^\s@]+@[^\s@]+\.[^\s@]+\s*$/.test(texto)
}

function ORastroDoGerenciador() {
  return (
    <p className={APOIO}>
      Este texto é um endereço de e-mail, e nenhuma peça de processo se chama
      assim: o gerenciador de senhas do seu navegador pode tê-lo escrito aqui
      sozinho. Limpe a busca — as peças continuam todas no processo.
    </p>
  )
}

function recortesDoProcesso(
  processo: ProcessoDoContrato,
  noProcesso: number,
): { chave: string; rotulo: string; contagem: number; falta?: string }[] {
  return [
    { chave: RECORTE_TUDO, rotulo: "Tudo", contagem: noProcesso },
    { chave: RECORTE_FISCALIZACAO, rotulo: "Fiscalização", contagem: processo.no_catalogo },
    {
      chave: TIPO_LAUDO,
      rotulo: "Laudos",
      contagem: processo.laudos,
      ...(processo.laudos === 0 ? { falta: "Falta um laudo de fiscalização neste processo" } : {}),
    },
  ]
}

function papeisNaOrdem(entradas: EntradaDoProcesso[]): { papel: string; rotulo: string }[] {
  const vistos = new Map<string, string>()
  for (const entrada of entradas) {
    if (!vistos.has(entrada.papel)) vistos.set(entrada.papel, entrada.rotulo_do_papel)
  }
  return [...vistos.entries()].map(([papel, rotulo]) => ({ papel, rotulo }))
}

export function emOrdemDecrescente(entradas: EntradaDoProcesso[]): EntradaDoProcesso[] {
  return entradas
    .map((entrada, indice) => ({ entrada, indice }))
    .sort((a, b) => {
      const da = a.entrada.data
      const db = b.entrada.data
      if (da === null && db === null) return a.indice - b.indice
      if (da === null) return 1
      if (db === null) return -1
      if (da !== db) return da < db ? 1 : -1
      return b.indice - a.indice
    })
    .map(({ entrada }) => entrada)
}

function ListaDeEntradas({
  entradas,
  abertaId,
  busca,
  aoAbrir,
}: {
  entradas: EntradaDoProcesso[]
  abertaId: string | null
  busca: string
  aoAbrir: (entrada: EntradaDoProcesso) => void
}) {
  const referencia = useRef<HTMLOListElement>(null)

  useEffect(() => {
    const lista = referencia.current
    const item = lista?.querySelector<HTMLElement>('[data-aberta="sim"]')
    if (!lista || !item) return
    const topo = item.offsetTop
    const fundo = topo + item.offsetHeight
    if (topo < lista.scrollTop) lista.scrollTop = topo
    else if (fundo > lista.scrollTop + lista.clientHeight) lista.scrollTop = fundo - lista.clientHeight
  }, [abertaId])

  if (entradas.length === 0) {
    if (busca !== "") return <p className={cn(VAZIO, "p-3 text-center")}>Não encontrado</p>
    return <p className={cn(VAZIO, "p-3")}>Nenhuma peça neste recorte.</p>
  }

  const grupos: { dia: string; itens: EntradaDoProcesso[] }[] = []
  for (const entrada of entradas) {
    const quando = entrada.data === null ? "sem data" : dia(entrada.data)
    const ultimo = grupos[grupos.length - 1]
    if (ultimo && ultimo.dia === quando) ultimo.itens.push(entrada)
    else grupos.push({ dia: quando, itens: [entrada] })
  }

  return (
    <LinhaDoTempo data-slot="esteira-lista" className="relative min-h-0 flex-auto overflow-y-auto" ref={referencia}>
      {grupos.map((grupo, indice) => (
        <GrupoDaLinhaDoTempo key={`${grupo.dia}-${indice}`} data={grupo.dia}>
          {grupo.itens.map((entrada) => {
            const tipo = entrada.tipo_descricao ?? `Tipo ${entrada.cd_tipo_documento}`
            const detalhe = [
              entrada.numero === null ? null : `nº ${numeroComAno(entrada.numero, entrada.ano)}`,
              entrada.autor_nome,
            ].filter((parte): parte is string => parte !== null && parte !== "")
            return (
              <ItemDaLinhaDoTempo
                key={chaveDaEntrada(entrada)}
                ativo={chaveDaEntrada(entrada) === abertaId}
                onClick={() => aoAbrir(entrada)}
                sobretitulo={
                  entrada.tipo_entrada === TIPO_LAUDO ? (
                    <>
                      <Icones.Laudo titulo="Laudo de fiscalização" />
                      {tipo}
                    </>
                  ) : entrada.ordem === null ? (
                    tipo
                  ) : (
                    `${entrada.ordem}º ${tipo}`
                  )
                }
                hora={entrada.data === null ? null : hora(entrada.data)}
                titulo={<span className={TEXTO_DO_CADASTRO}>{entrada.titulo ?? "Peça sem título"}</span>}
                detalhe={detalhe.length === 0 ? null : <span className={TEXTO_DO_CADASTRO}>{detalhe.join(" · ")}</span>}
                marcas={<MarcasDaPeca entrada={entrada} />}
              />
            )
          })}
        </GrupoDaLinhaDoTempo>
      ))}
    </LinhaDoTempo>
  )
}

function MarcasDaPeca({ entrada }: { entrada: EntradaDoProcesso }) {
  const marcas: React.ReactNode[] = []
  if (entrada.tipo_entrada === TIPO_LAUDO) {
    marcas.push(<Selo key="laudo" situacao="laudo" />)
    if (entrada.laudo_situacao !== null) {
      marcas.push(
        <Selo
          key="assinatura"
          situacao={chaveConhecida(entrada.laudo_situacao, "nao_configurado")}
          titulo={entrada.laudo_remessa_detalhe ?? undefined}
        />,
      )
    }
  }
  if (entrada.cancelada) {
    marcas.push(
      <Selo key="cancelada" situacao="cancelada" />,
    )
  }
  if (entrada.juntada && entrada.tipo_entrada !== TIPO_LAUDO) {
    marcas.push(
      <Selo key="juntada" situacao="juntada" />,
    )
  }
  if (entrada.tipo_entrada !== TIPO_LAUDO) {
    marcas.push(
      entrada.pdf_conferido_em !== null ? (
        <Selo
          key="conferido"
          situacao="conferida"
          titulo={`Conferido em ${dataHora(entrada.pdf_conferido_em)}: alguém abriu este documento neste sistema, e a cópia aberta ficou registrada`}
        />
      ) : (
        <Selo key="nao-conferido" situacao="nao_conferida" />
      ),
    )
  }
  for (const marca of entrada.marcas) {
    marcas.push(
      <Selo
        key={`${marca.tipo}-${marca.laudo_id ?? ""}`}
        situacao={chaveConhecida(marca.tipo, "informacao")}
        titulo={porQuemEQuando(marca.texto, marca.por, marca.em)}
      >
        {marca.rotulo}
      </Selo>,
    )
  }
  if (marcas.length === 0) return null
  return <>{marcas}</>
}

function porQuemEQuando(
  texto: string | null,
  por: string | null,
  em: string | null,
): string {
  const partes: string[] = []
  if (texto !== null && texto !== "") partes.push(texto)
  if (por !== null) partes.push(em === null ? `por ${por}` : `por ${por} em ${dataHora(em)}`)
  return partes.join(" · ")
}

type AbaDaPeca = "documento" | "ficha" | "avaliacao"

type Recusa = { titulo: string; regra: string; acao: string }

function recusaDe(falha: unknown): Recusa {
  if (falha instanceof ErroDaApi) {
    const titulo = falha.codigo === 404 ? "Peça não encontrada" : "Documento indisponível"
    return { titulo, regra: falha.regra, acao: falha.acao }
  }
  return {
    titulo: "Documento indisponível",
    regra:
      falha instanceof Error ? falha.message : "não foi possível abrir o documento",
    acao: "",
  }
}

function PainelDaPeca({
  contratoId,
  cpfNaSessao,
  peca,
  posicao,
  total,
  lembranca,
  aoAndar,
  aoAvaliar,
  aoConferir,
}: {
  contratoId: number
  cpfNaSessao: string
  peca: EntradaDoProcesso
  posicao: number
  total: number
  lembranca: Lembranca
  aoAndar: (direcao: -1 | 1) => void
  aoAvaliar: () => void
  aoConferir: (peca: EntradaDoProcesso) => void
}) {
  const ehLaudo = peca.tipo_entrada === TIPO_LAUDO
  const identificadorDaPeca = `peca-${peca.tipo_entrada}-${peca.id}`
  const [aba, definirAba] = useState<AbaDaPeca>("documento")
  const [endereco, definirEndereco] = useState<string | null>(null)
  const [carregando, definirCarregando] = useState(false)
  const [recado, definirRecado] = useState<Recusa | null>(null)
  const [fonte, definirFonte] = useState<FonteDoArquivo | null>(null)

  useEffect(() => {
    definirAba("documento")
  }, [peca.tipo_entrada, peca.id])

  useEffect(() => {
    const chave = `${peca.tipo_entrada}-${peca.id}`

    const guardado = lembranca.current?.get(chave)
    if (guardado !== undefined) {
      const endereco_lembrado = URL.createObjectURL(guardado.arquivo)
      definirRecado(null)
      definirCarregando(false)
      definirEndereco(endereco_lembrado)
      definirFonte(guardado.fonte)
      return () => URL.revokeObjectURL(endereco_lembrado)
    }

    let vivo = true
    let criado: string | null = null
    const controle = new AbortController()
    definirCarregando(true)
    definirRecado(null)
    definirEndereco(null)
    definirFonte(null)

    const buscar = ehLaudo ? baixarLaudo : baixarPeca
    buscar(contratoId, peca.id, controle.signal)
      .then(({ arquivo, endereco: novo, fonte: veio_de }) => {
        criado = novo
        guardarNaLembranca(lembranca, chave, { arquivo, fonte: veio_de })
        if (!ehLaudo) aoConferir(peca)
        if (!vivo) {
          URL.revokeObjectURL(novo)
          return
        }
        definirEndereco(novo)
        definirFonte(veio_de)
      })
      .catch((falha: unknown) => {
        if (!vivo || foiAbortado(falha)) return
        definirRecado(recusaDe(falha))
      })
      .finally(() => {
        if (vivo) definirCarregando(false)
      })

    return () => {
      vivo = false
      controle.abort()
      if (criado !== null) URL.revokeObjectURL(criado)
    }
  }, [contratoId, peca.id, peca.tipo_entrada, ehLaudo, lembranca])

  const abasDaPeca: DescricaoDeAba<AbaDaPeca>[] = [
    { chave: "documento", rotulo: "Documento" },
    { chave: "ficha", rotulo: ehLaudo ? "Ficha do laudo" : "Ficha da peça" },
    ...(ehLaudo
      ? []
      : [
          {
            chave: "avaliacao" as const,
            rotulo:
              peca.marcas.length > 0
                ? `Avaliação do fiscal · ${peca.marcas[0]?.rotulo ?? ""}`
                : "Avaliação do fiscal",
          },
        ]),
  ]

  return (
    <section
      className="flex min-w-0 flex-auto flex-col [&>header+div]:mb-0!"
      aria-label={peca.titulo ?? "Peça do processo"}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-(--borda) px-4 py-3 min-[1101px]:flex-nowrap">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-2">
          <span data-slot="ordem-da-peca" className="font-bold text-(--acao) tabular-nums">
            {ehLaudo ? (
              <Icones.Laudo titulo="Laudo de fiscalização" tamanho={18} />
            ) : peca.ordem === null ? (
              "—"
            ) : (
              `${peca.ordem}º`
            )}
          </span>
          <h2 className={MAIUSCULA}>{peca.titulo ?? "Peça sem título"}</h2>
          <Selo situacao="papel">{peca.rotulo_do_papel}</Selo>
        </div>
        <div className="flex flex-wrap items-center gap-2 min-[1101px]:flex-none">
          <span className="text-(length:--texto-xs) whitespace-nowrap text-(--tinta-3) tabular-nums">
            {posicao + 1} de {total}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn("h-7 text-xs", BOTAO_BRANCO)}
            onClick={() => aoAndar(-1)}
            disabled={posicao <= 0}
          >
            <Icones.Anterior />
            Anterior
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn("h-7 text-xs", BOTAO_BRANCO)}
            onClick={() => aoAndar(1)}
            disabled={posicao >= total - 1}
          >
            Próxima
            <Icones.Proxima />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn("h-7 text-xs", BOTAO_BRANCO)}
            onClick={() =>
              void (ehLaudo
                ? abrirLaudo(contratoId, peca.id)
                : abrirPeca(contratoId, peca.id))
            }
          >
            <Icones.Abrir />
            Abrir em nova aba
          </Button>
        </div>
      </header>

      <Abas
        nome={identificadorDaPeca}
        rotulo="O que ver desta peça"
        abas={abasDaPeca}
        atual={aba}
        aoTrocar={definirAba}
        classe="pe-4"
      />

      <PainelDaAba
        nome={identificadorDaPeca}
        chave="documento"
        ativa={aba === "documento"}
        classe={MIOLO}
      >
        {aba === "documento" &&
          (carregando ? (
            <p className={cn(VAZIO, NO_MIOLO)}>
              {peca.no_acervo_local
                ? "Abrindo o documento..."
                : "Buscando o documento no ATHUS. Pode levar alguns segundos."}
            </p>
          ) : recado !== null ? (
            <div className={cn(FAIXA_CRITICA, NO_MIOLO)} role="alert">
              <strong>{recado.titulo}</strong>
              <p className={MAIUSCULA}>{recado.regra}</p>
              {recado.acao !== "" && <p className={cn("text-(--tinta-2)", MAIUSCULA)}>{recado.acao}</p>}
            </div>
          ) : endereco !== null ? (
            <object
              className="aspect-[210/297] min-h-[78vh] w-full flex-none border-0 bg-(--fundo)"
              data={endereco}
              type="application/pdf"
              aria-label={`Documento ${peca.titulo ?? ""}`}
            >
              <p className={cn(VAZIO, NO_MIOLO)}>
                Este navegador não mostra o documento aqui. Use "Abrir em nova aba".
              </p>
            </object>
          ) : null)}
      </PainelDaAba>

      <PainelDaAba
        nome={identificadorDaPeca}
        chave="ficha"
        ativa={aba === "ficha"}
        classe={MIOLO}
      >
        {aba === "ficha" &&
          (ehLaudo ? (
            <FichaDoLaudo laudo={peca} />
          ) : (
            <FichaDaPeca peca={peca} fonte={fonte} />
          ))}
      </PainelDaAba>

      {!ehLaudo && (
        <PainelDaAba
          nome={identificadorDaPeca}
          chave="avaliacao"
          ativa={aba === "avaliacao"}
          classe={MIOLO}
        >
          {aba === "avaliacao" && (
            <AvaliacaoDoFiscal
              contratoId={contratoId}
              cpfNaSessao={cpfNaSessao}
              peca={peca}
              aoAvaliar={aoAvaliar}
            />
          )}
        </PainelDaAba>
      )}
    </section>
  )
}

function AvaliacaoDoFiscal({
  contratoId,
  cpfNaSessao,
  peca,
  aoAvaliar,
}: {
  contratoId: number
  cpfNaSessao: string
  peca: EntradaDoProcesso
  aoAvaliar: () => void
}) {
  const [decisao, definirDecisao] = useState<string | null>(null)
  const [texto, definirTexto] = useState("")
  const [gravando, definirGravando] = useState(false)
  const [historico, definirHistorico] = useState<AvaliacaoNaTela[] | null>(null)
  const [janelaAberta, definirJanelaAberta] = useState(false)
  const botaoDeAssinar = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    let vivo = true
    api
      .avaliacoesDaPeca(contratoId, peca.id)
      .then((lista) => {
        if (vivo) definirHistorico(lista)
      })
      .catch(() => {
        if (vivo) definirHistorico([])
      })
    return () => {
      vivo = false
    }
  }, [contratoId, peca.id])

  useEffect(() => {
    definirJanelaAberta(false)
  }, [peca.id])

  const fecharAJanela = useCallback(() => {
    definirJanelaAberta(false)
    botaoDeAssinar.current?.focus()
  }, [])

  const registrar = (senha: string) => {
    if (decisao === null) return Promise.resolve()
    definirGravando(true)
    return api
      .avaliarPeca(contratoId, peca.id, decisao, texto, senha)
      .then((resposta) => {
        definirTexto("")
        definirDecisao(null)
        definirJanelaAberta(false)
        definirHistorico((antes) => [resposta.avaliacao, ...(antes ?? [])])
        aoAvaliar()
      })
      .finally(() => definirGravando(false))
  }

  const decisaoEscolhida = DECISOES.find((opcao) => opcao.valor === decisao) ?? null

  return (
    <div className="flex w-full flex-1 flex-col overflow-y-auto">
      <Card className="rounded-none border-0 py-3">
        <CardHeader className="gap-1">
          <CardTitle>Avaliação do fiscal</CardTitle>
          <CardDescription>
            O que você decidir sobre esta peça fica registrado nela e gera um
            laudo, assinado por você e juntado ao processo na ordem cronológica.
          </CardDescription>
        </CardHeader>
        <CardContent>
        <div data-slot="rotulo-do-campo" className="mt-1 flex items-center gap-1">
          <span id="rotulo-da-decisao" className={cn(ROTULO_DE_CAMPO, "mb-0")}>
            Decisão
          </span>
          <MarcaDeObrigatorio />
        </div>
        <div className="mb-3 mt-1.5 flex flex-wrap gap-2" role="group" aria-labelledby="rotulo-da-decisao">
          {DECISOES.map((opcao) => (
            <Button
              key={opcao.valor}
              type="button"
              size="sm"
              variant={decisao === opcao.valor ? COR_DA_DECISAO[opcao.valor].variant : "outline"}
              className={decisao === opcao.valor ? COR_DA_DECISAO[opcao.valor].className : BOTAO_BRANCO}
              aria-pressed={decisao === opcao.valor}
              title={opcao.titulo}
              onClick={() => definirDecisao(opcao.valor)}
            >
              <span data-slot="icone-da-decisao" className={cn("inline-flex", decisao !== opcao.valor && COR_DO_ICONE[opcao.valor])}>
                <opcao.Icone />
              </span>
              {opcao.rotulo}
            </Button>
          ))}
        </div>

        <label className="mt-3 block">
          <span className={ROTULO_DE_CAMPO}>O que ocorreu, ou por quê</span>
          <Textarea
            rows={4}
            value={texto}
            onChange={(evento) => definirTexto(evento.target.value)}
          />
        </label>

        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Button
            ref={botaoDeAssinar}
            type="button"
            size="sm"
            className={SUCESSO}
            disabled={decisao === null || gravando}
            onClick={() => definirJanelaAberta(true)}
          >
            <Icones.Assinar />
            Registrar e assinar o laudo
          </Button>
          {decisao === null && (
            <span className={APOIO}>Escolha uma das quatro decisões acima.</span>
          )}
        </div>
        </CardContent>
      </Card>

      {janelaAberta && decisaoEscolhida !== null && (
        <JanelaDeAssinatura
          decisao={decisaoEscolhida}
          peca={peca}
          cpfNaSessao={cpfNaSessao}
          aoFechar={fecharAJanela}
          aoConfirmar={registrar}
          aoDevolverFoco={() => botaoDeAssinar.current?.focus()}
        />
      )}

      <Card className="flex-1 rounded-none border-0 border-t border-(--borda)">
        <CardHeader>
          <CardTitle>Histórico desta peça</CardTitle>
        </CardHeader>
        <CardContent>
        {historico === null ? (
          <p className={VAZIO}>Carregando o histórico...</p>
        ) : historico.length === 0 ? (
          <p className={VAZIO}>Esta peça ainda não foi avaliada.</p>
        ) : (
          <Cronologia aria-label="Histórico desta peça">
            {historico.map((avaliacao) => (
              <ItemDaCronologia
                key={avaliacao.id}
                quando={dia(avaliacao.avaliado_em)}
                detalheDoQuando={hora(avaliacao.avaliado_em)}
                titulo={<span className={TEXTO_DO_CADASTRO}>{avaliacao.avaliado_por_nome ?? "—"}</span>}
                descricao={
                  <>
                    <p className={cn("m-0", MAIUSCULA)}>{avaliacao.texto ?? "Sem texto registrado."}</p>
                    {avaliacao.alerta_da_verificacao !== null && (
                      <p className={cn(APOIO, MAIUSCULA)}>{avaliacao.alerta_da_verificacao}</p>
                    )}
                  </>
                }
                marcas={
                  <>
                    <Selo situacao={chaveConhecida(avaliacao.decisao, "informacao")}>
                      {avaliacao.rotulo_da_decisao}
                    </Selo>
                    {avaliacao.laudo_id !== null && <Selo situacao="laudo">Laudo assinado</Selo>}
                  </>
                }
              />
            ))}
          </Cronologia>
        )}
        </CardContent>
      </Card>
    </div>
  )
}

function JanelaDeAssinatura({
  decisao,
  peca,
  cpfNaSessao,
  aoFechar,
  aoConfirmar,
  aoDevolverFoco,
}: {
  decisao: (typeof DECISOES)[number]
  peca: EntradaDoProcesso
  cpfNaSessao: string
  aoFechar: () => void
  aoConfirmar: (senha: string) => Promise<unknown>
  aoDevolverFoco: () => void
}) {
  return (
    <ConfirmarComSenha
      aberto
      titulo="Assinar o laudo desta peça"
      resumo={
        <ResumoDoAto
          itens={[
            ["Decisão", decisao.rotulo],
            [
              "Peça",
              `${peca.titulo ?? "Peça sem título"}${
                peca.numero !== null ? ` · nº ${numeroComAno(peca.numero, peca.ano)}` : ""
              }`,
            ],
          ]}
        />
      }
      rotuloDaConta="Fiscal:"
      cpf={cpfNaSessao}
      explicacao="Sua senha confirma a assinatura do laudo — ela diz que é você quem está assinando agora."
      rotuloDoAto="Assinar o laudo"
      iconeDoAto={<Icones.Assinar />}
      classeDoAto={SUCESSO}
      aoConfirmar={aoConfirmar}
      aoFechar={aoFechar}
      aoDevolverFoco={aoDevolverFoco}
    />
  )
}

function FichaDoLaudo({ laudo }: { laudo: EntradaDoProcesso }) {
  return (
    <FichaEmLinhas titulo="Dados do laudo" subtitulo="Assinatura, autenticidade e integridade do arquivo">
      <Linha rotulo="Número">{laudo.numero}</Linha>
      <Linha rotulo="Tipo">{laudo.tipo_descricao}</Linha>
      <Linha rotulo="Assinado por">{laudo.autor_nome}</Linha>
      <Linha rotulo="Assinado em">
        {laudo.pdf_conferido_em === null ? null : dataHora(laudo.pdf_conferido_em)}
      </Linha>
      <Linha rotulo="Nível da assinatura">
        {nivelDeAssinatura(laudo.laudo_nivel)}
      </Linha>
      <Linha rotulo="Situação">
        {laudo.laudo_situacao === null ? null : (
          <Selo situacao={chaveConhecida(laudo.laudo_situacao, "nao_configurado")} />
        )}
      </Linha>
      <Linha rotulo="Juntado no processo em">
        {laudo.data === null ? null : dataHora(laudo.data)}
      </Linha>
      <Linha rotulo="Tamanho">
        {laudo.tamanho_bytes === null ? null : tamanho(laudo.tamanho_bytes)}
      </Linha>
      <Linha rotulo="Remessa ao processo">
        {laudo.laudo_remessa_detalhe}
      </Linha>
      <CodigoEmDestaque rotulo="Código de autenticidade">{laudo.laudo_codigo}</CodigoEmDestaque>
      <CodigoEmDestaque rotulo="Código de conferência do arquivo">{laudo.resumo_sha256}</CodigoEmDestaque>
    </FichaEmLinhas>
  )
}

function FichaDaPeca({
  peca,
  fonte,
}: {
  peca: EntradaDoProcesso
  fonte: FonteDoArquivo | null
}) {
  return (
    <FichaEmLinhas titulo="Dados da peça no processo" subtitulo="Rastreabilidade da origem e integridade do arquivo">
      <Linha rotulo="Tipo">
        {peca.tipo_descricao && <span className={TEXTO_DO_CADASTRO}>{peca.tipo_descricao}</span>}
      </Linha>
      <Linha rotulo="Código no ATHUS">{String(peca.cd_tipo_documento)}</Linha>
      <Linha rotulo="Papel">{peca.rotulo_do_papel}</Linha>
      <Linha rotulo="No catálogo de fiscalização">
        {peca.no_catalogo ? "Sim" : "Não"}
      </Linha>
      <Linha rotulo="Número">{peca.numero}</Linha>
      <Linha rotulo="Ano">{peca.ano === null ? null : String(peca.ano)}</Linha>
      <Linha rotulo="Data no processo">
        {peca.data === null ? null : dataHora(peca.data)}
      </Linha>
      <Linha rotulo="Autor">
        {peca.autor_nome && <span className={TEXTO_DO_CADASTRO}>{peca.autor_nome}</span>}
      </Linha>
      <Linha rotulo="Assinaturas">
        {peca.qtd_assinados === null ? null : inteiro(peca.qtd_assinados)}
      </Linha>
      <Linha rotulo="Tamanho">
        {peca.tamanho_bytes === null ? null : tamanho(peca.tamanho_bytes)}
      </Linha>
      <Linha rotulo="Conferido em">
        {peca.pdf_conferido_em === null ? null : dataHora(peca.pdf_conferido_em)}
      </Linha>
      <Linha rotulo="Fundamento do arquivo">
        {fonte !== null ? (
          <Selo situacao="fonte_do_arquivo">{ROTULO_DA_FONTE[fonte]}</Selo>
        ) : (
          peca.pdf_origem_rotulo
        )}
      </Linha>
      <CodigoEmDestaque rotulo="Código de conferência do arquivo">{peca.resumo_sha256}</CodigoEmDestaque>
    </FichaEmLinhas>
  )
}

function FichaEmLinhas({ titulo, subtitulo, children }: { titulo: string; subtitulo: string; children: React.ReactNode }) {
  return (
    <div className="w-full flex-1 overflow-y-auto bg-(--superficie-2)">
      <Card className="mx-auto h-full w-full rounded-none border-0 pt-3 pb-6">
        <CardHeader className="gap-1 border-b border-(--borda) [.border-b]:pb-3">
          <CardTitle className="tracking-(--tracking-rotulo)">{titulo}</CardTitle>
          <CardDescription>{subtitulo}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="m-0 divide-y divide-(--borda)">{children}</dl>
        </CardContent>
      </Card>
    </div>
  )
}

function vazio(conteudo: React.ReactNode): boolean {
  return conteudo === null || conteudo === undefined || conteudo === "" || conteudo === false
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div data-slot="linha-da-ficha" className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="flex-none text-(length:--texto-sm) text-(--tinta-2)">{rotulo}</dt>
      <dd className="m-0 min-w-0 text-right text-(length:--texto-sm) font-semibold text-(--tinta) tabular-nums wrap-anywhere">
        {vazio(children) ? "—" : children}
      </dd>
    </div>
  )
}

function CodigoEmDestaque({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div data-slot="chave-em-destaque" className="flex flex-col gap-1.5 py-2.5">
      <dt className="text-(length:--texto-sm) text-(--tinta-2)">{rotulo}</dt>
      <dd className="m-0 rounded-(--raio-controle) border border-(--borda) bg-(--painel-alt) p-2 font-mono text-(length:--texto-xs) text-(--tinta) break-all">
        {vazio(children) ? "—" : children}
      </dd>
    </div>
  )
}
